import type { DelegateArchive, DelegateTimelineEntry } from "./types";
import { mergeTimelineEntries, safeTimelineImage, validArchive, type TimelineScope } from "./delegateTimeline";
import { validClaudeArchive } from "./claudeTimeline";

export type PublicTimelineSource = {
  scope: TimelineScope; archive?: DelegateArchive; entries: DelegateTimelineEntry[];
  displayName: "Astra" | "Claude" | "Opus"; active?: boolean; phase?: string;
  terminalStatus?: "Stopped" | "Not completed" | "Updates saved";
};
export type ScopedPublicUpdate = { key:string; entry:DelegateTimelineEntry; scope:TimelineScope };
export type PublicTimelineSegment = {
  key:string; provider:"astra"|"claude"; displayName:"Astra"|"Claude"|"Opus";
  items:ScopedPublicUpdate[]; active:boolean; phase?:string;
};
const ID = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/;
export const publicSourceKey = (scope:TimelineScope) =>
  [scope.provider ?? "astra",scope.companyId,scope.requestMessageId,scope.runId].join(":");

/** Provider/run sequence is a deterministic tie-break, not a claimed causal order. */
export function groupRequestPublicUpdates(owner:{companyId:string;requestMessageId:string},
  sources:PublicTimelineSource[], previous:PublicTimelineSegment[] = []):PublicTimelineSegment[] {
  const events = new Map<string,ScopedPublicUpdate & {source:PublicTimelineSource}>();
  for (const source of sources) {
    const {scope,archive} = source;
    const provider = scope.provider ?? "astra";
    if (scope.companyId !== owner.companyId || scope.requestMessageId !== owner.requestMessageId ||
      !ID.test(scope.runId) || !["astra","claude"].includes(provider) ||
      (archive && (!validArchive(archive) || archive.runId !== scope.runId ||
        archive.requestMessageId !== scope.requestMessageId ||
        (provider === "claude" ? !validClaudeArchive(archive) : "provider" in archive)))) continue;
    const key = publicSourceKey(scope);
    for (const entry of mergeTimelineEntries([],source.entries)) {
      if (!Number.isFinite(Date.parse(entry.updatedAt)) ||
        (entry.sequence !== undefined && (!Number.isSafeInteger(entry.sequence) || entry.sequence < 1))) continue;
      if (entry.type === "image") {
        if (!entry.sequence) continue;
        if (!(entry.imageStatus === "expired" && !entry.image) && (!safeTimelineImage(entry.image?.url,scope) ||
          new URLSearchParams(entry.image!.url.split("?")[1]).get("sequence") !== String(entry.sequence))) continue;
      } else if ((entry.type !== undefined && entry.type !== "commentary") ||
        typeof entry.text !== "string" || !entry.text.trim() || entry.text.length > 3000) continue;
      const identity = entry.sequence !== undefined ? "sequence:" + entry.sequence :
        "fallback:" + entry.updatedAt + ":" + entry.text;
      const safeEntry = entry.imageStatus === "expired" ? {...entry,text:"Images cleared after 7 days",image:undefined} : entry;
      events.set(key + ":" + identity,{key:key + ":" + identity,entry:safeEntry,scope,source});
    }
  }
  const ordered = [...events.values()].sort((a,b) => Date.parse(a.entry.updatedAt)-Date.parse(b.entry.updatedAt) ||
    publicSourceKey(a.scope).localeCompare(publicSourceKey(b.scope)) ||
    (a.entry.sequence ?? 0)-(b.entry.sequence ?? 0) || a.key.localeCompare(b.key));
  const oldOwner = new Map<string,string>();
  for (const group of previous) for (const item of group.items) oldOwner.set(item.key,group.key);
  const used = new Set<string>(), groups:PublicTimelineSegment[] = [];
  for (const event of ordered) {
    const provider = event.scope.provider ?? "astra";
    let group = groups.at(-1);
    if (!group || group.provider !== provider) {
      group = {key:event.key,provider,displayName:provider === "astra" ? "Astra" : "Opus",items:[],active:false};
      groups.push(group);
    }
    group.items.push({key:event.key,entry:event.entry,scope:event.scope});
    // Unknown/native non-Opus records remain Claude, without making a new group.
    if (provider === "claude" && event.source.displayName !== "Opus") group.displayName = "Claude";
  }
  // Preserve an existing segment's identity across appended/hydrated events.
  // Only a genuine interleaving split needs a new segment key.
  for (const group of groups) {
    const old = group.items.map(item => oldOwner.get(item.key)).find(key => key && !used.has(key));
    if (old) group.key = old;
    used.add(group.key);
  }
  for (const source of sources.filter(source => source.active)) {
    const key = publicSourceKey(source.scope);
    const last = [...groups].reverse().find(group => group.items.some(item => publicSourceKey(item.scope) === key));
    if (last) { last.active=true; last.phase=source.phase; }
  }
  return groups;
}
